/**
 * F20 (Wave 2D) — admin cross-venue payments activity feed.
 *
 * Route: `/payments`
 * Permission: `payments:read` (super_admin + support + finance).
 *
 * Trust-based marking (Decision 1) → this feed is INFORMATIONAL only. Admins
 * see every mark / claim / refund / admin-override across the platform for
 * incident response + reconciliation. There is NO worklist action panel here;
 * any admin override goes through F17's `bookings:override` force-transition
 * route on the per-booking detail page.
 *
 * Filter sidebar: venue (text id), method, date range, actor_kind, overdue
 * toggle (Decision 8 — overdue is a filter, not a workflow).
 *
 * Cursor-paginated 50/page via the Load-more pattern shipped by the
 * venue-claims queue.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useI18n } from '@bravemove/i18n/react';
import {
  Badge,
  Banner,
  Card,
  Checkbox,
  Chip,
  CurrencyDisplay,
  DateRangePicker,
  EmptyState,
  FormField,
  Select,
  Skeleton,
  Table,
  TextInput,
  type DateRange,
  type TableColumn,
} from '@bravemove/ui-web';
import { UIIcon } from '@bravemove/design-tokens/icons';
import {
  PAYMENT_MARK_ACTOR_KINDS,
  PAYMENT_METHODS,
  PAYMENT_STATUS_VALUES,
  type AdminPageMeta,
  type PaymentMarkActorKind,
  type PaymentMethod,
  type PaymentStatus,
} from '@bravemove/types';
import {
  adminPaymentsApi,
  type PaymentActivityOpts,
  type PaymentActivityRow,
} from '../../api/payments';
import { translateApiError } from '../../auth/errors';
import { PageHeader } from '../../components/PageHeader';
import { AdminPagination } from '../../components/AdminPagination';

const PAGE_LIMIT = 25;

function buildActivityOpts(raw: PaymentActivityOpts): PaymentActivityOpts {
  const opts: Record<string, string | number | boolean> = { perPage: PAGE_LIMIT };
  if (raw.venueId) opts.venueId = raw.venueId;
  if (raw.method) opts.method = raw.method;
  if (raw.actorKind) opts.actorKind = raw.actorKind;
  if (raw.toStatus) opts.toStatus = raw.toStatus;
  if (raw.dateFrom) opts.dateFrom = raw.dateFrom;
  if (raw.dateTo) opts.dateTo = raw.dateTo;
  if (raw.overdue) opts.overdue = true;
  if (raw.page) opts.page = raw.page;
  return opts as unknown as PaymentActivityOpts;
}

function formatRelative(iso: string, locale: string): string {
  const target = new Date(iso).getTime();
  if (Number.isNaN(target)) return iso;
  const diffMs = target - Date.now();
  const diffMin = Math.round(diffMs / 60_000);
  try {
    const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
    const absMin = Math.abs(diffMin);
    if (absMin < 60) return rtf.format(diffMin, 'minute');
    const diffHour = Math.round(diffMin / 60);
    if (Math.abs(diffHour) < 24) return rtf.format(diffHour, 'hour');
    const diffDay = Math.round(diffHour / 24);
    return rtf.format(diffDay, 'day');
  } catch {
    return iso;
  }
}

function dateOnlyToISO(yyyymmdd: string, endOfDay = false): string {
  // value comes in as `YYYY-MM-DD` from DateRangePicker; expand to a full
  // ISO timestamp the server (`z.string().datetime({ offset: true })`) accepts.
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(yyyymmdd);
  if (!m) return yyyymmdd;
  const [, y, mo, d] = m;
  const time = endOfDay ? '23:59:59.999Z' : '00:00:00.000Z';
  return `${y}-${mo}-${d}T${time}`;
}

function isoToDateOnly(iso: string): string {
  // Reverse for the DateRangePicker which expects `YYYY-MM-DD`.
  return iso.slice(0, 10);
}

/**
 * BP-16 — the backend Zod schema requires `venueId` to be a UUID; anything
 * else 422s and blanks the whole feed. Gate the filter client-side.
 */
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function PaymentsActivityFeedPage() {
  const { t, locale } = useI18n();
  const [searchParams, setSearchParams] = useSearchParams();

  const venueIdParam = searchParams.get('venueId') ?? '';
  const methodParam = (searchParams.get('method') as PaymentMethod | null) ?? '';
  const actorKindParam = (searchParams.get('actorKind') as PaymentMarkActorKind | null) ?? '';
  const toStatusParam = (searchParams.get('toStatus') as PaymentStatus | null) ?? '';
  const dateFromParam = searchParams.get('dateFrom') ?? '';
  const dateToParam = searchParams.get('dateTo') ?? '';
  const overdueParam = searchParams.get('overdue') === 'true';
  const pageParam = Math.max(Number.parseInt(searchParams.get('page') ?? '1', 10) || 1, 1);

  // Local-only state for the venueId input (debounced into URL on blur/Enter).
  const [venueIdInput, setVenueIdInput] = useState(venueIdParam);
  const [venueIdError, setVenueIdError] = useState<string | null>(null);
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setVenueIdInput(venueIdParam);
  }, [venueIdParam]);

  const [rows, setRows] = useState<readonly PaymentActivityRow[]>([]);
  const [pagination, setPagination] = useState<AdminPageMeta | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchList = useCallback(
    async (opts: PaymentActivityOpts) => {
      setLoading(true);
      setRows([]);
      setError(null);
      try {
        const data = await adminPaymentsApi.listActivity(opts);
        setRows(data.items);
        setPagination(data.pagination ?? null);
      } catch (err) {
        setError(translateApiError(err, t));
      } finally {
        setLoading(false);
      }
    },
    [t],
  );

  useEffect(() => {
    const opts: Record<string, string | boolean | number> = {};
    if (venueIdParam) opts.venueId = venueIdParam;
    if (methodParam) opts.method = methodParam;
    if (actorKindParam) opts.actorKind = actorKindParam;
    if (toStatusParam) opts.toStatus = toStatusParam;
    if (dateFromParam) opts.dateFrom = dateFromParam;
    if (dateToParam) opts.dateTo = dateToParam;
    if (overdueParam) opts.overdue = true;
    opts.page = pageParam;
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void fetchList(buildActivityOpts(opts as PaymentActivityOpts));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    venueIdParam,
    methodParam,
    actorKindParam,
    toStatusParam,
    dateFromParam,
    dateToParam,
    overdueParam,
    pageParam,
  ]);

  function updateParam(key: string, value: string | null): void {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value) next.set(key, value);
      else next.delete(key);
      next.delete('page');
      return next;
    });
  }

  /**
   * BP-16 — validate before committing to the URL: a non-UUID value would
   * 422 server-side and blank the whole feed behind a generic error banner.
   */
  function commitVenueId(): void {
    const trimmed = venueIdInput.trim();
    if (trimmed && !UUID_RE.test(trimmed)) {
      setVenueIdError(
        t('payment:filter.venue_invalid', {
          defaultValue: 'Enter a full venue UUID (8-4-4-4-12 hex characters).',
        }),
      );
      return;
    }
    setVenueIdError(null);
    updateParam('venueId', trimmed || null);
  }

  function goToPage(page: number): void {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (page <= 1) next.delete('page');
      else next.set('page', String(page));
      return next;
    });
  }

  function onDateRangeChange(range: DateRange): void {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (range.start) next.set('dateFrom', dateOnlyToISO(range.start, false));
      else next.delete('dateFrom');
      if (range.end) next.set('dateTo', dateOnlyToISO(range.end, true));
      else next.delete('dateTo');
      next.delete('page');
      return next;
    });
  }

  const methodOptions = useMemo(
    () => [
      { value: '', label: t('payment:filter.method_all', { defaultValue: 'Any method' }) },
      ...PAYMENT_METHODS.map((m) => ({
        value: m,
        label: t(`payment:method.${m}`, {
          defaultValue:
            m === 'cash' ? 'Cash' : m === 'whish' ? 'Whish' : m === 'omt' ? 'OMT' : 'Other',
        }),
      })),
    ],
    [t],
  );

  const actorOptions = useMemo(
    () => [
      { value: '', label: t('payment:filter.actor_all', { defaultValue: 'Any actor' }) },
      ...PAYMENT_MARK_ACTOR_KINDS.map((a) => ({
        value: a,
        label: t(`payment:filter.actor_${a}`, {
          defaultValue:
            a === 'venue_staff'
              ? 'Venue staff'
              : a === 'customer'
                ? 'Customer'
                : a === 'admin'
                  ? 'Administrator'
                  : 'System',
        }),
      })),
    ],
    [t],
  );

  const toStatusOptions = useMemo(
    () => [
      { value: '', label: t('payment:filter.status_all', { defaultValue: 'Any status' }) },
      ...PAYMENT_STATUS_VALUES.map((s) => ({ value: s, label: s })),
    ],
    [t],
  );

  const dateRange: DateRange = useMemo(() => {
    const start = dateFromParam ? isoToDateOnly(dateFromParam) : undefined;
    const end = dateToParam ? isoToDateOnly(dateToParam) : undefined;
    return { start, end };
  }, [dateFromParam, dateToParam]);

  const columns = useMemo<readonly TableColumn<PaymentActivityRow>[]>(
    () => [
      {
        key: 'booking',
        header: t('payment:activity.column_booking', { defaultValue: 'Booking' }),
        cell: (row) => (
          <div className="flex flex-col gap-0.5">
            <span className="font-mono text-[11.5px] text-ink-3">
              {row.booking?.id ?? row.bookingId}
            </span>
            <span className="text-[12px] text-ink">{row.booking?.venueName ?? row.venueId}</span>
          </div>
        ),
      },
      {
        key: 'actor',
        header: t('payment:activity.column_actor', { defaultValue: 'Actor' }),
        cell: (row) => (
          <div className="flex flex-col gap-0.5">
            <Badge tone="neutral" size="sm">
              {row.actorKind}
            </Badge>
            {row.actorDisplayName ? (
              <span className="text-[12px] text-ink-2">{row.actorDisplayName}</span>
            ) : null}
          </div>
        ),
      },
      {
        key: 'transition',
        header: t('payment:activity.column_transition', { defaultValue: 'Transition' }),
        cell: (row) => (
          <div className="flex items-center gap-1.5 flex-wrap">
            <Chip>{row.fromStatus}</Chip>
            <span aria-hidden="true" className="text-ink-3 font-mono">
              →
            </span>
            <Chip>{row.toStatus}</Chip>
          </div>
        ),
      },
      {
        key: 'method_ref',
        header: t('payment:activity.column_method', { defaultValue: 'Method' }),
        cell: (row) => (
          <div className="flex flex-col gap-0.5">
            {row.method ? (
              <Badge tone="info" size="sm">
                {row.method}
              </Badge>
            ) : (
              <span className="text-[12px] text-ink-3">—</span>
            )}
            {row.reference ? (
              <span className="font-mono text-[11px] text-ink-3">{row.reference}</span>
            ) : null}
          </div>
        ),
      },
      {
        key: 'amount',
        header: t('payment:activity.column_amount', { defaultValue: 'Amount' }),
        cell: (row) =>
          row.booking ? (
            <CurrencyDisplay
              amount={row.booking.priceAmount}
              currency={row.booking.priceCurrency}
            />
          ) : (
            <span className="text-[12px] text-ink-3">—</span>
          ),
      },
      {
        key: 'when',
        header: t('payment:activity.column_when', { defaultValue: 'When' }),
        cell: (row) => (
          <span className="text-[12px] text-ink-2">{formatRelative(row.createdAt, locale)}</span>
        ),
      },
    ],
    [t, locale],
  );

  return (
    <div className="space-y-6" data-testid="payments-activity-feed-page">
      <PageHeader
        title={t('payment:activity.title', { defaultValue: 'Payments activity' })}
        subtitle={t('payment:activity.subtitle', {
          defaultValue:
            'Every payment mark across the platform. Informational only — no actions here.',
        })}
      />

      <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-4">
        {/* Filter sidebar */}
        <Card>
          <div className="flex flex-col gap-3" data-testid="payments-filter-sidebar">
            <h2 className="text-h2 font-bold text-ink">
              {t('payment:activity.filters_title', { defaultValue: 'Filters' })}
            </h2>

            <FormField
              label={t('payment:filter.venue_label', { defaultValue: 'Venue id' })}
              error={venueIdError ?? undefined}
            >
              <TextInput
                value={venueIdInput}
                onChange={(e) => {
                  setVenueIdInput(e.target.value);
                  if (venueIdError) setVenueIdError(null);
                }}
                onBlur={commitVenueId}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') commitVenueId();
                }}
                placeholder={t('payment:filter.venue_placeholder', { defaultValue: 'venue-uuid' })}
                data-testid="payments-filter-venue"
              />
            </FormField>

            <FormField label={t('payment:filter.method_label', { defaultValue: 'Method' })}>
              <Select
                value={methodParam}
                onChange={(e) => updateParam('method', e.target.value || null)}
                options={methodOptions}
                data-testid="payments-filter-method"
              />
            </FormField>

            <FormField label={t('payment:filter.actor_label', { defaultValue: 'Actor' })}>
              <Select
                value={actorKindParam}
                onChange={(e) => updateParam('actorKind', e.target.value || null)}
                options={actorOptions}
                data-testid="payments-filter-actor"
              />
            </FormField>

            <FormField label={t('payment:filter.to_status_label', { defaultValue: 'To status' })}>
              <Select
                value={toStatusParam}
                onChange={(e) => updateParam('toStatus', e.target.value || null)}
                options={toStatusOptions}
                data-testid="payments-filter-to-status"
              />
            </FormField>

            <FormField label={t('payment:filter.date_range_label', { defaultValue: 'Date range' })}>
              <DateRangePicker value={dateRange} onChange={onDateRangeChange} />
            </FormField>

            <label className="flex items-center gap-2 text-[12.5px] text-ink cursor-pointer">
              <Checkbox
                checked={overdueParam}
                onChange={(e) => updateParam('overdue', e.target.checked ? 'true' : null)}
                data-testid="payments-filter-overdue"
              />
              <span>
                {t('payment:filter.overdue_label', {
                  defaultValue: 'Only show completed + unpaid (overdue)',
                })}
              </span>
            </label>
          </div>
        </Card>

        {/* Activity feed */}
        <div className="flex flex-col gap-3">
          {error ? <Banner tone="danger">{error}</Banner> : null}

          {loading ? (
            <div className="space-y-3" data-testid="payments-loading">
              {Array.from({ length: 6 }, (_, i) => (
                <Skeleton key={i} className="h-14 w-full rounded-lg" />
              ))}
            </div>
          ) : rows.length === 0 ? (
            <EmptyState
              icon={<UIIcon name="wallet" size={36} color="currentColor" />}
              title={t('payment:activity.empty_title', {
                defaultValue: 'No payment activity matches these filters',
              })}
              description={t('payment:activity.empty_body', {
                defaultValue: 'Try widening the date range or clearing filters.',
              })}
            />
          ) : (
            <Table
              data={rows as PaymentActivityRow[]}
              columns={columns as TableColumn<PaymentActivityRow>[]}
              rowKey={(row) => row.id}
              empty={t('payment:activity.empty_title', { defaultValue: 'No activity' })}
            />
          )}

          {pagination && !loading && rows.length > 0 ? (
            <AdminPagination
              pagination={pagination}
              shownCount={rows.length}
              onChange={goToPage}
              testId="payments-pagination"
            />
          ) : null}
        </div>
      </div>
    </div>
  );
}

export default PaymentsActivityFeedPage;
